import { HeartIcon as HeartIconOutline } from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import { useState } from "react";
import { Tooltip } from "react-tooltip";

import { UnsaveConversionGroupModal } from "@/app/modals/UnsaveConversionGroupModal";
import { UnsaveConversionModal } from "@/app/modals/UnsaveConversionModal";
import { UnsaveIsolationModal } from "@/app/modals/UnsaveIsolationModal";
import { changeSavedConversionAction } from "@/data/conversion/actions/changeSavedConversionAction";
import { changeSavedConversionGroupAction } from "@/data/conversion/actions/changeSavedConversionGroupAction";
import { changeSavedIsolationAction } from "@/data/isolation/actions/changeSavedIsolationAction";
import { ConversionGroupConstructedNoDates } from "@/types/defs/conversionGroupConstructedNoDates";
import {
  ConversionConstructedNoDates,
  IsolationConstructedNoDates,
} from "@/types/types";
import { logError } from "@/utils/logError";

type SaveAudioBase = {
  confirmOnUnsave: boolean;
  className?: string;
};

export function SaveAudio({
  type,
  entity,
  confirmOnUnsave,
  className,
}: SaveAudioBase &
  (
    | {
        type: "conversion";
        entity: ConversionConstructedNoDates;
      }
    | {
        type: "isolation";
        entity: IsolationConstructedNoDates;
      }
    | {
        type: "conversionGroup";
        entity: ConversionGroupConstructedNoDates;
      }
  )) {
  const [optimisticData, changeOptimisticSaved] = useState(entity);
  const [unsaveModalOpen, setUnsaveModalOpen] = useState(false);

  const onSaveAudio = async (saved: boolean) => {
    const previousSaved = optimisticData.saved;
    changeOptimisticSaved({ ...optimisticData, saved });
    try {
      switch (type) {
        case "conversion":
          await changeSavedConversionAction({
            conversionId: optimisticData.id,
            saved: saved,
          });
          break;
        case "isolation":
          await changeSavedIsolationAction({
            isolationId: optimisticData.id,
            saved: saved,
          });
          break;
        case "conversionGroup":
          await changeSavedConversionGroupAction({
            conversionGroupId: optimisticData.id,
            saved: saved,
          });
          break;
      }
    } catch (error) {
      changeOptimisticSaved({ ...optimisticData, saved: previousSaved });
      logError(error);
    }
  };

  const handleSaveToggle = () => {
    if (!optimisticData.saved) {
      onSaveAudio(true);
      return;
    }

    if (confirmOnUnsave) {
      setUnsaveModalOpen(true);
      return;
    }

    onSaveAudio(false);
  };

  const renderModal = () => {
    switch (type) {
      case "conversion":
        return (
          <UnsaveConversionModal
            open={unsaveModalOpen}
            closeModal={() => setUnsaveModalOpen(false)}
            unsaveConversion={() => onSaveAudio(false)}
          />
        );
      case "isolation":
        return (
          <UnsaveIsolationModal
            open={unsaveModalOpen}
            closeModal={() => setUnsaveModalOpen(false)}
            unsaveIsolation={() => onSaveAudio(false)}
          />
        );
      case "conversionGroup":
        return (
          <UnsaveConversionGroupModal
            open={unsaveModalOpen}
            closeModal={() => setUnsaveModalOpen(false)}
            unsaveConversionGroup={() => onSaveAudio(false)}
          />
        );
    }
  };

  return (
    <div className="flex items-center">
      {renderModal()}
      <button
        type="button"
        onClick={handleSaveToggle}
        className={className}
        aria-label={optimisticData.saved ? "Unsave" : "Save to library"}
      >
        {optimisticData.saved ?
          <HeartIconSolid
            data-tooltip-id={`unsave-${entity.id}`}
            data-tooltip-content="Unsave"
            data-tooltip-place="top"
            className="h-6 w-6 fill-primary stroke-primary"
          />
        : <HeartIconOutline
            data-tooltip-id={`save-${entity.id}`}
            data-tooltip-content="Save to library"
            data-tooltip-place="top"
            className="h-6 w-6 stroke-gray-900"
          />
        }
      </button>
      <Tooltip id={`save-${entity.id}`} />
      <Tooltip id={`unsave-${entity.id}`} />
    </div>
  );
}
